// Phase 4 synth UI — builds the full synth panel on top of the Phase 3
// design system. Sub-phase 4a populates the oscillator section only;
// middle (filter/env/LFO) and FX rack are placeholders for 4b/4c.

import { Synth } from "./audio/synth.ts";
import { PianoKeyboard } from "./ui/keyboard.ts";
import { OscModule } from "./ui/osc-module.ts";
import { FilterModule } from "./ui/filter-module.ts";
import { EnvModule } from "./ui/env-module.ts";
import { ModEnvModule } from "./ui/mod-env-module.ts";
import { LfoModule } from "./ui/lfo-module.ts";
import { EqModule } from "./ui/eq-module.ts";
import { DistortionModule } from "./ui/distortion-module.ts";
import { DelayModule } from "./ui/delay-module.ts";
import { ReverbModule } from "./ui/reverb-module.ts";
import { MasterModule } from "./ui/master-module.ts";
import { FxRack, type FxRackItem } from "./ui/fx-rack.ts";
import { Knob } from "./ui/knob.ts";
import { PresetManager } from "./preset/manager.ts";
import { PresetBar } from "./ui/preset-bar.ts";
import { MidiManager, type MidiInputInfo } from "./ui/midi.ts";
import { MidiPlayer } from "./preset/midi-player.ts";
import { MidiPlayerWidget } from "./ui/midi-player.ts";
import { BUNDLED_MIDI_CLIPS } from "./preset/midi-clips.ts";
import { Dropdown, type DropdownOption } from "./ui/dropdown.ts";

type ElAttrs<K extends keyof HTMLElementTagNameMap> = Partial<
  HTMLElementTagNameMap[K]
> & { class?: string };

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: ElAttrs<K> = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (key === "class" && typeof value === "string") {
      node.className = value;
    } else if (value !== undefined) {
      (node as unknown as Record<string, unknown>)[key] = value;
    }
  }
  for (const child of children) node.append(child);
  return node;
}

function setStatus(root: HTMLElement, msg: string, kind: "info" | "ok" | "error" = "info"): void {
  const slot = root.querySelector<HTMLElement>(".synth-boot-status");
  if (!slot) return;
  slot.textContent = msg;
  const wrap = slot.closest<HTMLElement>(".synth-boot");
  if (wrap) wrap.dataset["status"] = kind;
}

// Global error funnel — without iOS Safari DevTools the user has no other
// way to see what failed. Routes uncaught errors and rejected promises
// into the visible boot-status line.
function installGlobalErrorReporting(root: HTMLElement): void {
  window.addEventListener("error", (ev) => {
    const msg = ev.error instanceof Error ? ev.error.message : ev.message || "unknown error";
    setStatus(root, `error: ${msg}`, "error");
  });
  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev.reason;
    const msg = reason instanceof Error ? reason.message : String(reason);
    setStatus(root, `error: ${msg}`, "error");
  });
}

async function boot(): Promise<void> {
  const root = document.getElementById("synth-root");
  if (!root) return;

  mountHeader(root);
  installGlobalErrorReporting(root);

  if (!self.crossOriginIsolated) {
    setStatus(
      root,
      "cross-origin isolation off — SharedArrayBuffer required for audio. check COOP/COEP headers.",
      "error",
    );
    return;
  }

  setStatus(root, "loading worklet + wasm…");
  let synth: Synth;
  try {
    synth = await Synth.create();
  } catch (err) {
    console.error(err);
    setStatus(
      root,
      `synth init failed: ${err instanceof Error ? err.message : String(err)}`,
      "error",
    );
    return;
  }

  setStatus(root, "ready — play to begin.", "ok");
  // Diagnostic hook — allows test scripts and DevTools to inspect engine
  // state. Intentionally a `window` global, not exported through any
  // public API. Stripped trivially by minifiers in a future production
  // bundle if it bothers anyone.
  (window as unknown as { __synthDebug?: Synth }).__synthDebug = synth;
  mountPanel(root, synth);
}

function mountHeader(root: HTMLElement): void {
  root.append(
    el("header", { class: "synth-header" }, [
      el("h1", {}, ["antinode — synth"]),
      el("div", { class: "synth-boot" }, [
        el("span", { class: "synth-boot-status" }, ["loading…"]),
      ]),
    ]),
  );
}

function mountPanel(root: HTMLElement, synth: Synth): void {
  const panel = el("div", { class: "synth-panel" });

  // Forward-declared so the wrapped setParam/setDiscrete below can call
  // `manager.markDirty()` after the manager is constructed. We build the
  // manager only after every module exists (it needs refs to all of
  // them), but the modules' onChange wires need a stable function
  // reference now — hence the let + nullable pattern.
  let manager: PresetManager | null = null;
  let tempoKnob: Knob | undefined;
  const setParam = (path: string, value: number): void => {
    synth.setParam(path, value);
    manager?.markDirty();
  };
  const setDiscrete = (path: string, value: number): void => {
    synth.setDiscrete(path, value);
    manager?.markDirty();
  };

  // Preset bar sits at the top of the panel, spanning all columns. Host
  // is appended first so the bar mounts above the section grid; CSS
  // assigns the `preset` grid area.
  const presetHost = el("div", { class: "synth-section synth-section-preset" });
  panel.append(presetHost);

  // Oscillator section — two stacked OSC modules plus the amp envelope.
  // The amp env lives with the oscillators because it shapes their
  // output amplitude end-to-end, and grouping it here keeps the mid
  // column (filter / mod env / LFO) at a more readable height.
  const oscSection = el("div", { class: "synth-section synth-section-osc" }, [
    el("p", { class: "synth-section-label" }, ["oscillators · amp env"]),
  ]);
  panel.append(oscSection);

  const osc1 = new OscModule({
    container: oscSection,
    index: 1,
    setParam,
    setDiscrete,
    initial: { shape: 0, octave: 0, detuneCents: 0, level: 0.8, enabled: true },
  });

  const osc2 = new OscModule({
    container: oscSection,
    index: 2,
    setParam,
    setDiscrete,
    initial: { shape: 0, octave: 0, detuneCents: 0, level: 0, enabled: false },
  });

  const ampEnv = new EnvModule({
    container: oscSection,
    title: "amp env",
    pathBase: "amp_env",
    setParam,
    initial: { attackMs: 5, decayMs: 120, sustain: 0.6, releaseMs: 200 },
  });

  // Middle section — filter + modulation sources (mod env, LFO). Amp
  // env moved to the oscillator column because it modulates the
  // oscillator output amplitude; keeping it there makes the mid
  // section dedicated to filter + modulation.
  const midSection = el("div", { class: "synth-section synth-section-mid" }, [
    el("p", { class: "synth-section-label" }, ["filter · modulation"]),
  ]);
  panel.append(midSection);

  const filter = new FilterModule({
    container: midSection,
    setParam,
    setDiscrete,
    initial: { cutoffHz: 1500, resonance: 0.707, mode: "lp", drive: 0 },
  });

  const modEnv = new ModEnvModule({
    container: midSection,
    setParam,
    setDiscrete,
    initial: {
      attackMs: 5,
      decayMs: 200,
      sustain: 0,
      releaseMs: 200,
      amount: 0,
      destination: "none",
    },
  });

  const lfo = new LfoModule({
    container: midSection,
    setParam,
    setDiscrete,
    initial: {
      shape: "sine",
      rateHz: 1,
      rateSyncIndex: 2, // 1/4
      sync: false,
      amount: 0,
      destination: "none",
    },
  });

  // FX section — being built top-down in sub-phase 4c (eq, distortion,
  // delay, reverb, master gain + meter). Each FX module lives inside an
  // FxRack that owns drag-to-reorder; placeholders for not-yet-built
  // modules sit OUTSIDE the rack so they don't accidentally get drag
  // handles or participate in reordering.
  const fxSection = el("div", { class: "synth-section synth-section-fx" }, [
    el("p", { class: "synth-section-label" }, ["fx · master"]),
  ]);
  panel.append(fxSection);

  const fxRackHost = el("div", { class: "synth-fx-rack-host" });
  fxSection.append(fxRackHost);

  // Build each FX module against a detached container so we can collect
  // the root elements and hand them to the rack, which will own their
  // DOM order from this point forward.
  const eqHost = document.createElement("div");
  const eq = new EqModule({
    container: eqHost,
    setParam,
    initial: { lowGainDb: 0, midFreqHz: 1000, midGainDb: 0, midQ: 0.707, highGainDb: 0 },
  });
  const distHost = document.createElement("div");
  const distortion = new DistortionModule({
    container: distHost,
    setParam,
    setDiscrete,
    initial: { driveDb: 0, mix: 0, curve: "soft" },
  });
  const delayHost = document.createElement("div");
  const delay = new DelayModule({
    container: delayHost,
    setParam,
    initial: {
      timeLMs: 380,
      timeRMs: 520,
      feedback: 0.35,
      crossFeedback: 0.15,
      toneHz: 6000,
      mix: 0,
    },
  });
  const reverbHost = document.createElement("div");
  const reverb = new ReverbModule({
    container: reverbHost,
    setParam,
    initial: { size: 0.55, damping: 0.4, mix: 0 },
  });

  // Helper that unwraps the single child each detached host now holds —
  // the module's root element. Returning the raw root keeps the rack's
  // DOM clean (no synthetic wrapper levels).
  const unwrap = (host: HTMLElement): HTMLElement => host.firstElementChild as HTMLElement;

  const rackItems: FxRackItem[] = [
    { id: "eq", label: "eq", element: unwrap(eqHost) },
    { id: "distortion", label: "distortion", element: unwrap(distHost) },
    { id: "delay", label: "delay", element: unwrap(delayHost) },
    { id: "reverb", label: "reverb", element: unwrap(reverbHost) },
  ];

  const fxRack = new FxRack({
    container: fxRackHost,
    items: rackItems,
    onReorder: (order) => {
      // Engine's FxSlot indices: 0=Eq, 1=Distortion, 2=Delay, 3=Reverb.
      // Push one setDiscrete per slot so the engine's `fx.order` array
      // mirrors the new id sequence.
      const idToSlot: Record<string, number> = {
        eq: 0,
        distortion: 1,
        delay: 2,
        reverb: 3,
      };
      for (let i = 0; i < order.length; i++) {
        const slot = idToSlot[order[i]!];
        if (slot !== undefined) setDiscrete(`fx.slot_${i}`, slot);
      }
    },
  });

  const master = new MasterModule({
    container: fxSection,
    setParam,
    setDiscrete,
    sampleTap: (n) => synth.sampleTap(n),
    initial: { gain: 0.7, mono: true },
  });

  // Keyboard band spans the panel's bottom.
  const keysSection = el("div", { class: "synth-section synth-section-keys" }, [
    el("div", { class: "synth-keys" }, [
      el("canvas", { class: "synth-keys-canvas" }),
      el("p", { class: "synth-keys-hint" }, [
        el("span", {}, ["keys: a w s e d f t g y h u j k o l"]),
        el("span", {}, ["z / x — octave"]),
        el("span", { class: "synth-keys-octave" }, ["octave: 4"]),
      ]),
    ]),
  ]);
  panel.append(keysSection);

  // Audio start chip — once the user clicks it, the audio context resumes
  // and the synth becomes playable. Sits in the header's right side so it
  // stays unobtrusive yet always reachable.
  const startHost = root.querySelector<HTMLElement>(".synth-boot");
  // Input device dropdown slot is allocated here but populated later —
  // we need a `PianoKeyboard` reference for the MidiManager wiring,
  // and the piano is built after the panel mounts. Saving the slot up
  // front keeps the visual order (input | tempo | start) deterministic.
  let inputSlot: HTMLElement | null = null;
  if (startHost) {
    // Clip player — bundled + uploaded MIDI clips that loop through the
    // synth. Mounted first so the header reads status → clip → input
    // device → tempo, matching the user's flow (auto-play before
    // hardware input before performance control).
    const clipSlot = el("div", { class: "synth-boot-midi-clip" });
    startHost.append(clipSlot);
    const midiPlayer = new MidiPlayer({
      noteOn: (m, v) => synth.noteOn(m, v),
      noteOff: (m) => synth.noteOff(m),
      allNotesOff: () => synth.allNotesOff(),
    });
    new MidiPlayerWidget({
      container: clipSlot,
      player: midiPlayer,
      clips: BUNDLED_MIDI_CLIPS,
      onError: (msg) => setStatus(root, msg, "error"),
    });

    inputSlot = el("div", { class: "synth-boot-input" });
    startHost.append(inputSlot);

    // Tempo knob — global BPM. Lives left of the start-audio chip so
    // the eye reads "control values | transport actions" along the
    // header. The LFO uses this BPM when in sync mode; future
    // tempo-locked features (delays, arpeggiator, etc.) will too.
    const tempoSlot = el("div", { class: "synth-boot-tempo" });
    startHost.append(tempoSlot);
    tempoKnob = new Knob({
      container: tempoSlot,
      size: 40,
      min: 20,
      max: 300,
      default: 120,
      value: 120,
      step: 1,
      label: "tempo",
      format: (v) => `${Math.round(v)} bpm`,
      onChange: (v) => {
        setParam("bpm", v);
        // Tempo also drives MIDI clip playback speed — the player's
        // virtual clock advances at `bpm / fileBpm` per real second.
        midiPlayer.setBpm(v);
      },
    });
    // Push the initial BPM so an upload loaded before the knob is
    // touched still uses the current tempo.
    midiPlayer.setBpm(tempoKnob.current);

    // Audio-start affordance. The reliable iOS-Safari path is an explicit,
    // visible button that calls `ctx.resume()` synchronously inside its
    // pointerdown handler — document-level capture-phase listeners are
    // unreliable for gesture attribution on iOS. The chip lives inside
    // the boot status block and removes itself once the context is
    // running. Any other gesture path that ends up starting audio (e.g.
    // the QWERTY keyboard or a knob drag on desktop) will also remove
    // it via the `audioStarted` callback below.
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "synth-start-chip";
    chip.textContent = "tap to start";
    const statusSlot = startHost.querySelector<HTMLElement>(".synth-boot-status");
    if (statusSlot) {
      // The chip stands in for the status line while audio is suspended —
      // hiding the redundant "ready — play to begin." text keeps the header
      // at the same row count it'll have once audio is running, so removing
      // the chip doesn't reflow everything below it.
      statusSlot.dataset["chipActive"] = "1";
      statusSlot.insertAdjacentElement("afterend", chip);
    } else {
      startHost.prepend(chip);
    }

    let chipRemoved = false;
    const removeChip = (): void => {
      if (chipRemoved) return;
      chipRemoved = true;
      chip.remove();
      if (statusSlot) delete statusSlot.dataset["chipActive"];
    };

    const startAudio = (ev?: Event): void => {
      ev?.preventDefault();
      // `synth.start()` calls `ctx.resume()` synchronously before any
      // await — the resume is initiated in the gesture's execution
      // frame, which iOS treats as a valid activation.
      synth.start()
        .then(() => {
          removeChip();
          setStatus(root, `audio · ${synth.contextState}`, "ok");
        })
        .catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : String(err);
          setStatus(root, `audio failed: ${msg}`, "error");
        });
    };

    chip.addEventListener("pointerdown", startAudio);

    // Desktop fallback: if the user interacts with anything else first
    // (turning a knob, pressing a QWERTY letter), still resume the
    // context so they don't have to also tap the chip. Capture phase
    // so the originating gesture still reaches its intended target.
    const fallbackOpts: AddEventListenerOptions = { once: true, capture: true };
    const fallback = (): void => {
      if (chipRemoved) return;
      startAudio();
    };
    document.addEventListener("pointerdown", fallback, fallbackOpts);
    document.addEventListener("keydown", fallback, fallbackOpts);
  }

  root.append(panel);

  // Piano keyboard, wired to engine note events.
  const canvas = keysSection.querySelector<HTMLCanvasElement>(".synth-keys-canvas");
  const octaveLabel = keysSection.querySelector<HTMLElement>(".synth-keys-octave");
  let piano: PianoKeyboard | null = null;
  if (canvas) {
    piano = new PianoKeyboard({
      canvas,
      ...(octaveLabel ? { octaveLabel } : {}),
      initialOctave: 4,
      onNote: (event) => {
        if (event.kind === "trigger") {
          synth.noteOn(event.midi, event.velocity ?? 1.0);
        } else {
          synth.noteOff(event.midi);
        }
      },
    });
  }

  // MIDI input wiring — populates the input dropdown with detected
  // hardware MIDI devices. Selecting a device disables QWERTY input
  // so a connected controller doesn't double-trigger; selecting
  // "Computer keyboard" re-enables QWERTY and disconnects MIDI.
  // Quiet fallback if Web MIDI is unsupported / denied: the dropdown
  // just stays on QWERTY.
  if (inputSlot && piano) {
    wireInputSelector(inputSlot, piano);
  }

  // Push the OSC defaults to the engine so the audible state matches the
  // UI from the very first note.
  synth.setParam("osc1.shape", 0);
  synth.setParam("osc1.detune_cents", 0);
  synth.setParam("osc1.level", 0.8);
  synth.setDiscrete("osc1.octave", 0);
  synth.setDiscrete("osc1.enabled", 1);
  synth.setParam("osc2.shape", 0);
  synth.setParam("osc2.detune_cents", 0);
  synth.setParam("osc2.level", 0);
  synth.setDiscrete("osc2.octave", 0);
  synth.setDiscrete("osc2.enabled", 0);

  synth.setParam("filter.cutoff_hz", 1500);
  synth.setParam("filter.resonance", 0.707);
  synth.setDiscrete("filter.mode", 0); // LP
  synth.setParam("filter.drive", 0);

  synth.setParam("amp_env.attack_ms", 5);
  synth.setParam("amp_env.decay_ms", 120);
  synth.setParam("amp_env.sustain", 0.6);
  synth.setParam("amp_env.release_ms", 200);

  synth.setParam("mod_env.attack_ms", 5);
  synth.setParam("mod_env.decay_ms", 200);
  synth.setParam("mod_env.sustain", 0);
  synth.setParam("mod_env.release_ms", 200);
  synth.setParam("mod_amount", 0);
  synth.setDiscrete("mod_destination", 0); // None

  synth.setParam("lfo.shape", 0);
  synth.setParam("lfo.rate_hz", 1);
  synth.setParam("lfo.amount", 0);
  synth.setDiscrete("lfo.destination", 0); // None
  synth.setDiscrete("lfo.rate_mode", 0); // Hz
  synth.setDiscrete("lfo.rate_sync", 2); // 1/4

  synth.setParam("bpm", 120);

  synth.setParam("fx.eq.low_shelf_gain_db", 0);
  synth.setParam("fx.eq.mid_freq_hz", 1000);
  synth.setParam("fx.eq.mid_gain_db", 0);
  synth.setParam("fx.eq.mid_q", 0.707);
  synth.setParam("fx.eq.high_shelf_gain_db", 0);

  synth.setParam("fx.distortion.drive", 0);
  synth.setParam("fx.distortion.mix", 0);
  synth.setDiscrete("fx.distortion.curve", 0); // Soft

  synth.setParam("fx.delay.time_l_ms", 380);
  synth.setParam("fx.delay.time_r_ms", 520);
  synth.setParam("fx.delay.feedback", 0.35);
  synth.setParam("fx.delay.cross_feedback", 0.15);
  synth.setParam("fx.delay.tone_hz", 6000);
  synth.setParam("fx.delay.mix", 0);

  synth.setParam("fx.reverb.size", 0.55);
  synth.setParam("fx.reverb.damping", 0.4);
  synth.setParam("fx.reverb.mix", 0);

  synth.setParam("master_gain", 0.7);

  // Preset wiring — manager is constructed last because it needs every
  // module ref. The wrapped setParam/setDiscrete above will call
  // `manager.markDirty()` from this point on for any control change.
  // Factory presets snapshot the defaults pushed just above; applying
  // `init` returns the synth to that exact state.
  manager = new PresetManager({
    osc1,
    osc2,
    filter,
    ampEnv,
    modEnv,
    lfo,
    eq,
    distortion,
    delay,
    reverb,
    master,
    fxRack,
    getBpm: () => tempoKnob?.current ?? 120,
    setBpm: (bpm) => {
      // setValue fires the knob's onChange — that pushes the new BPM to
      // the engine and (via the wrapped setParam) would mark dirty. The
      // manager's `applying` guard prevents the mark during preset
      // load; for explicit user-side `setBpm` calls (none right now)
      // the mark would fire, which is correct.
      tempoKnob?.setValue(bpm);
    },
  });
  new PresetBar({ container: presetHost, manager });
}

/** Sentinel value used by the dropdown for the "Computer keyboard"
 *  (QWERTY) option. Real MIDI device ids are opaque platform-supplied
 *  strings and never collide with this literal. */
const QWERTY_VALUE = "qwerty";

function wireInputSelector(host: HTMLElement, piano: PianoKeyboard): void {
  let dropdown: Dropdown<string> | null = null;

  const buildOptions = (devices: MidiInputInfo[]): DropdownOption<string>[] => {
    const options: DropdownOption<string>[] = [
      { value: QWERTY_VALUE, label: "computer keyboard" },
    ];
    for (const d of devices) {
      const opt: DropdownOption<string> = { value: d.id, label: d.name };
      if (d.manufacturer) opt.hint = d.manufacturer;
      options.push(opt);
    }
    return options;
  };

  const apply = (value: string): void => {
    if (value === QWERTY_VALUE) {
      midi.selectInput(null);
      piano.setQwertyEnabled(true);
    } else {
      // Disable QWERTY so a connected controller doesn't double-trigger
      // notes (and so the QWERTY listener can't steal focus from a
      // hardware controller's primary path).
      piano.setQwertyEnabled(false);
      midi.selectInput(value);
    }
  };

  const midi = new MidiManager({
    piano,
    onDevicesChange: (devices) => {
      const options = buildOptions(devices);
      const current = dropdown?.getValue() ?? QWERTY_VALUE;
      const stillThere = options.some((o) => o.value === current);
      dropdown?.setOptions(options);
      // If the previously-selected device disappeared (hot-unplug),
      // fall back to QWERTY so the user always has an input path.
      if (!stillThere && current !== QWERTY_VALUE) {
        dropdown?.setValue(QWERTY_VALUE);
        apply(QWERTY_VALUE);
      }
    },
    onUnavailable: () => {
      // Quiet fallback — leave the dropdown showing just QWERTY, which
      // already works without MIDI permission.
    },
  });

  dropdown = new Dropdown<string>({
    container: host,
    options: buildOptions([]),
    value: QWERTY_VALUE,
    minWidth: 200,
    onChange: (value) => apply(value),
  });

  void midi.init();
}

void boot();
